/**
 * "Your captions are ready" on whatever page the host is on.
 *
 * The server sends `job:done` to every tab of the account that started a
 * caption render, AI clip ideas or a deck recording
 * (backend services/jobDoneService.js). Before this, the result only showed
 * on the page that was polling, so a host who moved on found out by going
 * back to look.
 *
 * Rules:
 *  - A page already polling that job shows its own result (utils/jobWatch),
 *    so it is skipped here.
 *  - Nothing in the broadcast studio: a toast over a live show is noise, and
 *    its "Open" button would navigate away, which ends the broadcast.
 *  - Several finishing together (a batch of auto-captions) become one toast.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from './ui/Toast';
import { isJobWatched } from '../utils/jobWatch';
import { jobDoneToast, isQuietPath } from '../utils/jobDone';

export const GROUP_MS = 2500;

export default function JobDoneNotifier() {
  const { socket } = useSocket();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const pendingRef = useRef([]);
  const timerRef = useRef(null);
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!socket) return undefined;
    const flush = () => {
      timerRef.current = null;
      const batch = pendingRef.current;
      pendingRef.current = [];
      if (!batch.length || isQuietPath(pathRef.current)) return;
      const t = jobDoneToast(batch);
      if (!t) return;
      toast.show({
        type: t.type,
        title: t.title,
        message: t.message,
        action: t.path ? { label: t.actionLabel, onClick: () => navigate(t.path) } : undefined,
      });
    };
    const onDone = (payload) => {
      if (!payload || !payload.id || !payload.kind) return;
      if (seenRef.current.has(payload.id)) return;
      seenRef.current.add(payload.id);
      if (seenRef.current.size > 200) seenRef.current = new Set([payload.id]);
      if (isJobWatched(payload.kind, payload.itemId)) return;
      pendingRef.current.push(payload);
      if (!timerRef.current) timerRef.current = setTimeout(flush, GROUP_MS);
    };
    socket.on('job:done', onDone);
    return () => {
      socket.off('job:done', onDone);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingRef.current = [];
    };
  }, [socket, toast, navigate]);

  return null;
}
